export const reality = `
# How the Mind Constructs Reality?

In this article, I explore how the human mind predicts the external world, how these predictions turn into emotions, actions, and experiences, and how the mind models and reshapes itself, accompanied by various theoretical approaches.
## Does Perception Derive from Reality or the Mind?

> **“The purpose of our brain is to survive by predicting the future.”**

First, to understand the relationship between the human mind and reality, I will take a closer look at Karl Friston’s _Free-Energy_ principle.

The brain does not passively receive sensory information from the external world.
On the contrary, it interprets incoming data through **pre-established predictions**.

— _“The brain is a prediction machine.”_

According to Friston’s _free-energy_ model, the brain is **not a blank slate (tabula rasa)**.
The brain is born with **roughly pre-structured** predictive mechanisms due to genetics.

That is, a baby is not born knowing everything, but comes with **prior tendencies** regarding **what is meaningful to learn for survival**.

> _Just like how the eye is born sensitive to light, and the ear to sound frequencies…_

A newborn looks with its eyes but does not yet understand what it sees; it hears sounds but does not know who they come from.

Here, the brain begins to gather **statistical patterns**. Every piece of data is accompanied by a prediction:

> “Is this the same as the previous one?”
> “What happens next with this?”

Through this process, internal models are formed. Every new experience tests the old prediction; if there is a surprise, the model is updated. The surprise is a prediction error.

So we can say this:

Our brain does not understand the external world innately, but it is **born with an architecture ready to understand and inclined to make predictions.**

These predictions are based on past experiences, learned patterns, and our internal models.

These are our mental “world.”

**According to Robert Greene’s book “The Laws of Human Nature,”** experiences, learned patterns, and expectations are formed as follows:

Social relationships experienced in childhood and the responses from authority figures help the individual learn what is right or dangerous, and over time, these learned reactions become automatic.

Learned patterns cause people to react without thinking based on previously acquired schemas, and they guide their decisions through these automatic behaviors.

Expectations, on the other hand, are formed from the traces of past positive or negative experiences. According to Greene, if a person received a reward or acceptance in a certain situation, they develop an expectation of receiving the same outcome in a similar situation. Over time, these expectations become part of the personality and behavioral repertoire.

At this point, we ask the following question:
**“How well does this internal prediction model we’ve built align with external reality?”**

This is where the **free-energy principle** comes into play.

According to Karl Friston:

> **Free energy** is a measure of the difference between the brain’s predictions and the real data coming from the senses.

The greater this difference, the higher the likelihood of the brain encountering a “surprise.”

And surprise means energy loss. It means a risk to survival.
Therefore, the brain **must minimize surprise.**
Just like a system seeking balance, _it must constantly adjust itself to reality._

Friston’s model also states that the brain is not only a “prediction-making” system, but also a system that **tries to fulfill its prediction**.

In other words:

The brain creates an **internal model**: You are walking down a path. The ground has always been flat, and your steps have followed the same rhythm.
Your mind says: “The next step will also be on solid ground.”

With each step, the model’s prediction accuracy is confirmed.

But this time, there’s a small hole you didn’t notice before. You place your foot down — and suddenly, it hits empty space.
“The ground I expected isn’t here!”

This small surprise sends the following message to the brain’s internal model:
“This path isn’t like it used to be. There’s something new. Update.”

The brain is now more cautious with the next step.
The model has changed. The expectation has changed.
With the hole now detected, **the surprise decreases**, adaptation increases, and energy is preserved.

This process is called **Active Inference**.
The brain doesn’t just understand, it _tests its understanding_.

## What About Being Aware of Our Thoughts?

Perception is made up of senses and predictions, _but what about being aware of the prediction itself?_

Here enters the concept of: **Metacognition.**
That is: **“Being aware of your own mental processes.”**
You not only **think** about something,
you also realize and question **how you think, why you think that way, and whether you’re thinking correctly**.

> “The brain also reviews the model it builds,
> and remodels within its own inner world.”

The individual is aware of what they know, how they think, and which strategies they use. They critically evaluate their own thought processes. As a result of this awareness and evaluation, the person directs their own thinking. They plan, change strategies, correct mistakes, and restructure their flow of thought to reach their goals.

They don’t just say, _“This is how I think”;_
they also say, _“What is that person thinking?”, “How do they feel?”,_
even, _“What might they be thinking about me?”_ — making multilayered inferences.

In psychology, this ability is called **Theory of Mind**.
From empathy to strategic thinking, storytelling to social cooperation, many human capabilities are based on this mental modeling capacity.

## Thinking Beyond Time

The human mind does not only perceive the present moment;
it also **returns to the past, constructs the future, and imagines possibilities that have never occurred.**

This ability is not only for acquiring information, but also for **modeling, making sense of, and navigating the self and the world**.

Dreaming is necessary not only for the future, but also for **defining oneself**.

> “What kind of person do I want to be?”
> “Who was I in the past, who am I now?”

Such dreams form the mental core of our identity.

## The mind needs a higher model in the face of complexity.

As knowledge increases, uncertainty grows, misleading paths multiply, and the question “What is true knowledge?” becomes more dominant.
Here, the mind often creates either an **inner wise voice** (conscience, intuition, superego) or turns to an **external guide**:

> **In its search for knowledge, the human mind seeks not only its own reason but a higher reason — an authority, a sage.**

Various theories have been proposed on this matter. One of the most comprehensive is the **Epistemic Authority Theory**, developed by philosopher **Linda Trinkaus Zagzebski**.

According to this theory, if an individual **trusts their own intellectual processes conscientiously and consciously**, then it is also rational to trust another person who is similarly intellectually responsible and trustworthy.
According to Zagzebski, such trust is not the abandonment of reason; rather, it is **the mind recognizing its own limitations and turning toward a more inclusive structure of knowledge.**

In other words:

The mind does not merely want to produce knowledge —
it also seeks **reliable mental compasses to reach “true knowledge.”**

This compass may sometimes be:

- A scientific authority,
- A teacher,
- A philosopher,
- Or an internalized “wise self.”

## The Mind Constructs Emotion

**Emotions arise through the process of appraisal.**
This approach is one of the most widely accepted models among cognitive theories of emotion and originates from psychologist **Richard Lazarus**.

According to Lazarus, what determines what an event means to a person is **not the event itself**, but how the individual **appraises** it.
This theory was first extensively developed under the title **“Cognitive-Motivational-Relational Theory of Emotion,”** and later expanded by many studies.

The mind not only constructs meaning, but also constructs feeling. After the process of thought and modeling is complete, the mind produces an **emotional output** that aligns with this model.

One of the most powerful theoretical explanations of this approach is the **Constructed Emotion Theory**, developed by neuroscientist **Lisa Feldman Barrett**.

_Emotions do not come directly from the external world; they are constructed by the mind through interpreting the perceived situation, relating it to past experiences, and making sense of it within the present context._

To illustrate how emotion is “built inside rather than outside,” let’s look at the same sentence spoken by _two different people_.

**Sentence:**
“You wouldn’t understand even if I explained.”

**1. Person: Ayşe**
— Someone who has often belittled you and dismissed your opinions.

**Past:** She had previously ignored my thoughts.
**Context:** I am currently seeking support on something important.
**Model:** She is devaluing me again.
**Emotion:** Anger, hurt, sense of exclusion

**2. Person: Deniz**
— A well-meaning friend who cares about you and usually explains things calmly.

**Past:** She always tries to be clear but sometimes explains things in a complicated way.
**Context:** We’re discussing a technical topic, and she’s a bit confused.
**Model:** She’s thinking, “This might really be hard to explain.”
**Emotion:** Slight disappointment and calmness

Emotion is a motivating force. It ranks the importance of a situation:

> “This matters, I should respond.” or “This is harmless, never mind.”

Action is a behavioral plan aligned with the mental model.
Action produces results.
The mind observes this outcome and receives **feedback**.

> Was my expectation correct?
> Did I encounter a surprise?

Thus, the mind **updates** its internal model. And the cycle begins again — but now the mind is **better equipped** than in the previous cycle.

## References

Barrett, L. F. (2017). _How emotions are made: The secret life of the brain._ Houghton Mifflin Harcourt.

Friston, K. (2010). The free-energy principle: A unified brain theory? _Nature Reviews Neuroscience, 11_(2), 127–138. [https://doi.org/10.1038/nrn2787](https://doi.org/10.1038/nrn2787)

Greene, R. (2018). _The laws of human nature._ Viking Press.

Lazarus, R. S. (1991). Emotion and adaptation. In Pervin, L. A. (Ed.), _Handbook of personality: Theory and research_ (pp. 609–637). New York: Oxford University Press.

Moors, A. (2013). Appraisal theory of emotion. In N. M. Seel (Ed.),
_Encyclopedia of the Sciences of Learning_ (pp. 243–245). Springer.
[https://doi.org/10.1007/978-1-4419-1428-6_493](https://doi.org/10.1007/978-1-4419-1428-6_493)

Nichols, S. (2014). _Boundaries of the mind: The individual in the fragile sciences: Cognition._ Oxford University Press.

Zagzebski, L. (2012). _Epistemic authority: A theory of trust, authority, and autonomy in belief._ Oxford University Press.

Veenman, M. V. J. (2013). Metacognition. In N. M. Seel (Ed.),
_Encyclopedia of the Sciences of Learning_ (pp. 2203–2206). Springer.
[https://doi.org/10.1007/978-1-4419-1428-6_493](https://doi.org/10.1007/978-1-4419-1428-6_493)
`;
